import {
  RefundModalPaymentIntentFragment,
  StripePaymentIntent,
  StripePaymentIntentStatus,
  StripeRefund,
} from 'shared/types/graphql';
import { getMonetaryDisplayValue } from '.';

export type GetPaymentIntentDetailsStripeRefund = Pick<StripeRefund, 'amount' | 'clientFeeRefunded'>;

export const getPaymentIntentDetails = (
  paymentIntent?: RefundModalPaymentIntentFragment | null,
): {
  statusLabel: string;
  color: string;
  refundedTotal: number;
  isRefunded: boolean;
  isRefundedPartially: boolean;
  totalRefundedClientFees: number;
  totalInitialRemainingBalance: string;
} => {
  /**
   * Number of cents that have been refunded back to the client
   */
  const refundedTotal = paymentIntent?.stripeRefunds?.length
    ? paymentIntent.stripeRefunds
        ?.map(({ amount }) => amount)
        .reduce((accumulator, currentValue) => accumulator + currentValue) || 0
    : 0;

  const isRefunded = Boolean(paymentIntent?.stripeRefunds?.length);

  const isRefundedPartially: boolean = isRefunded && refundedTotal !== paymentIntent?.amount;

  /**
   * Number of cents that have been refunded back to the client specifically coming from fees
   */
  const totalRefundedClientFees: number = paymentIntent?.stripeRefunds?.length
    ? paymentIntent.stripeRefunds.reduce(
        (accumulator, { clientFeeRefunded }) => accumulator + (clientFeeRefunded || 0),
        0,
      )
    : 0;

  /**
   * Return total for full total with fees
   * Return - if none are available
   */
  const totalInitialRemainingBalance: string = paymentIntent?.total
    ? getMonetaryDisplayValue(paymentIntent.total - refundedTotal - totalRefundedClientFees)
    : '—';

  const getStatusLabelAndColor = (
    paymentIntent?: Pick<StripePaymentIntent, 'status' | 'paidAt'> | null,
  ): { statusLabel: string; color: string } => {
    const defaultLabelAndColor = {
      statusLabel: 'Pending',
      color: '#edc20a',
    };

    if (!paymentIntent) return defaultLabelAndColor;

    const { status, paidAt } = paymentIntent;

    if (status === StripePaymentIntentStatus.Canceled) {
      return {
        statusLabel: 'Canceled',
        color: '#8f99a4',
      };
    }

    if (isRefundedPartially) {
      return {
        statusLabel: 'Partial Refund',
        color: '#e47792',
      };
    }

    if (isRefunded) {
      return {
        statusLabel: 'Refund',
        color: '#e47792',
      };
    }

    if (paidAt) {
      return {
        statusLabel: 'Complete',
        color: '#5cc098',
      };
    }

    return defaultLabelAndColor;
  };

  return {
    ...getStatusLabelAndColor(paymentIntent),
    refundedTotal,
    isRefunded,
    isRefundedPartially,
    totalRefundedClientFees,
    totalInitialRemainingBalance,
  };
};
